import { ApiError, type components, createServerApiClient, unwrap } from '@shared/api-client/index';
import { Button } from '@shared/ui/button/button.component';
import { Form } from '@shared/ui/form/form.component';
import { TextField } from '@shared/ui/form/text-field.component';
import { AppShell } from '@shared/ui/shell/app-shell.component';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData } from 'react-router';

import { apiOrigin } from '../core/api';
import { useMe, useSignIn, useSignOut } from '../core/session.hooks';
import type { Route } from './+types/home.page';
import styles from './home.page.module.css';

type User = components['schemas']['UserResponse'];

export function meta(_args: Route.MetaArgs) {
	return [{ title: 'Demo · KissCat' }, { name: 'description', content: 'KissCat demo app' }];
}

/**
 * SSR seed for the current user. Cross-origin in local dev (the session cookie
 * isn't on the app origin), so it can come back null there — the client query
 * revalidates on mount regardless.
 */
export async function loader({ request }: Route.LoaderArgs): Promise<{ user: User | null }> {
	const server = createServerApiClient(apiOrigin, request);
	try {
		return { user: unwrap(await server.GET('/v1/me')).user };
	} catch (error) {
		if (error instanceof ApiError && error.status === 401) return { user: null };
		throw error;
	}
}

export default function Home() {
	const { t } = useTranslation();
	const { user: initialUser } = useLoaderData<typeof loader>();
	const form = useForm<{ email: string }>({ defaultValues: { email: '' } });

	const { data: user } = useMe(initialUser);
	const signIn = useSignIn();
	const signOut = useSignOut();

	const onSignIn = ({ email }: { email: string }) =>
		signIn.mutate(email.trim(), { onSuccess: () => form.reset({ email: '' }) });

	return (
		<AppShell appName="demo">
			<div className={styles.demo}>
				<h1>{t('home.title', 'Demo')}</h1>

				{!user ? (
					<Form form={form} onSubmit={onSignIn} className={styles.signIn}>
						<TextField
							name="email"
							type="email"
							label={t('auth.email', 'Email')}
							placeholder="you@example.com"
							required
							rules={{ required: true }}
						/>
						<Button type="submit" disabled={signIn.isPending}>
							{t('auth.signInDev', 'Sign in (dev)')}
						</Button>
					</Form>
				) : (
					<>
						<p className={styles.who}>
							{t('auth.signedInAs', 'Signed in as')} <strong>{user.email}</strong>
							<Button variant="ghost" size="small" onClick={() => signOut.mutate()}>
								{t('auth.signOut', 'Sign out')}
							</Button>
						</p>
						<Link to="/todos">{t('nav.todos', 'Todos')}</Link>
					</>
				)}
			</div>
		</AppShell>
	);
}
